import { GenData, QSingle } from '@educraft/base';
import { CalcUtil, SimpleGenSingle, Numerals, Fraction } from '../../genEq/genHelper';

export const Combinatorics = {
  combination:
    () =>
    (data: GenData): QSingle => {
      const r = data.random;
      const n = r.next(5, 21);
      const k = r.next(2, Math.min(6, n - 2) + 1);
      return {
        texts: [
          `Kolika způsoby lze vybrat ${k} předmětů z ${n} předmětů, pokud na pořadí nezáleží`,
          'a každý předmět lze vybrat jen jednou?',
        ],
        answer: `${CalcUtil.binomial(n, k)}`,
      };
    },

  permutation:
    () =>
    (data: GenData): QSingle => {
      const r = data.random;
      const n = r.next(5, 10);
      return {
        texts: [
          `Kolika způsoby lze uspořádat ${n} předmětů, pokud na pořadí záleží`,
          'a každý předmět lze použít jen jednou?',
        ],
        answer: `${CalcUtil.factorial(n)}`,
      };
    },

  variation:
    () =>
    (data: GenData): QSingle => {
      const r = data.random;
      const n = r.next(5, 21);
      const k = r.next(2, Math.min(6, n - 2) + 1);
      return {
        texts: [
          `Kolika způsoby lze uspořádat ${k} předmětů z ${n} předmětů, pokud na pořadí záleží`,
          'a každý předmět lze použít jen jednou?',
        ],
        answer: `${CalcUtil.variation(n, k)}`,
      };
    },
};

export const Derivation = {
  base:
    (count: number) =>
    (data: GenData): QSingle => {
      const r = data.random;
      const numbers = Array.from({ length: count }, () => r.next(-5, 6));
      if (numbers[0] === 0) numbers[0] = 1;

      let sb1 = '';
      let sb2 = '';

      for (let i = 0; i < numbers.length; i++) {
        const y = count - i - 1;
        if (numbers[i] === 0) continue;

        if (y === 0) {
          sb1 += Numerals.strAdd(numbers[i]);
        } else if (y === 1) {
          sb1 += `${Numerals.strAddOne(numbers[i], i === 0)  }x`;
          sb2 += Numerals.strAddOne(numbers[i], i === 0);
        } else if (y === 2) {
          sb1 += `${Numerals.strAddOne(numbers[i], i === 0)  }x${Numerals.superScript(y)}`;
          sb2 += `${Numerals.strAddOne(numbers[i] * y, i === 0)  }x`;
        } else if (y > 0) {
          sb1 += `${Numerals.strAddOne(numbers[i], i === 0)  }x${Numerals.superScript(y)}`;
          sb2 += `${Numerals.strAddOne(numbers[i] * y, i === 0)  }x${Numerals.superScript(y - 1)}`;
        }
        sb1 += ' ';
        sb2 += ' ';
      }

      return {
        texts: [`f(x) = ${sb1.trim()}`, "f'(x) = ?"],
        answer: sb2.trim(),
      };
    },
};

export const EquationsAdv = {
  quadratic:
    () =>
    (data: GenData): QSingle => {
      const r = data.random;
      while (true) {
        const x1 = r.next(-10, 11);
        const x2 = r.next(-10, 11);
        if (x1 === x2) continue;

        const q = `x${Numerals.superScript(2)} ${Numerals.strAddOne(-(x1 + x2), false)}x ${Numerals.strAdd(x1 * x2)} = 0`;
        const results = [x1, x2].sort((a, b) => a - b);
        return {
          texts: [q],
          answer: `{${  results.join(', ')  }}`,
        };
      }
    },
};

export const Geometry = {
  pythagorasHypotenuse:
    () =>
    (data: GenData): QSingle => {
      const triples = [
        [3, 4, 5],
        [5, 12, 13],
        [7, 24, 25],
        [8, 15, 17],
        [9, 12, 15],
        [6, 8, 10],
        [10, 24, 26],
        [12, 16, 20],
        [15, 20, 25],
        [20, 21, 29],
      ];
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const item = triples[data.random.next(triples.length)];
      const unit = units[data.random.next(units.length)];
      return {
        texts: [
          `Pravoúhlý trojúhelník má odvěsny ${item[0]} ${unit} a ${item[1]} ${unit}.`,
          'Jak dlouhá je přepona?',
        ],
        answer: `${item[2]} ${unit}`,
      };
    },

  pythagorasLeg:
    () =>
    (data: GenData): QSingle => {
      const triples = [
        [3, 4, 5],
        [5, 12, 13],
        [7, 24, 25],
        [8, 15, 17],
        [9, 12, 15],
        [6, 8, 10],
        [10, 24, 26],
        [12, 16, 20],
        [15, 20, 25],
        [20, 21, 29],
      ];
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const item = triples[data.random.next(triples.length)];
      const unit = units[data.random.next(units.length)];
      return {
        texts: [
          `Pravoúhlý trojúhelník má odvěsnu ${item[0]} ${unit} a přeponu ${item[2]} ${unit}.`,
          'Jak dlouhá je druhá odvěsna?',
        ],
        answer: `${item[1]} ${unit}`,
      };
    },

  surfaceAreaCube:
    (inverse = false) =>
    (data: GenData): QSingle => {
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const unit = units[data.random.next(units.length)];
      const x = data.random.next(1, 21);
      if (inverse) {
        return {
          texts: [
            `Krychle má povrch ${6 * x * x} ${unit}${Numerals.superScript(2)}.`,
            'Jakou má délku strany?',
          ],
          answer: `${x} ${unit}`,
        };
      }
      return {
        texts: [`Krychle má délku strany ${x} ${unit}.`, 'Jaký má povrch pláště?'],
        answer: `${6 * x * x} ${unit}${Numerals.superScript(2)}`,
      };
    },

  volumeCube:
    (inverse = false) =>
    (data: GenData): QSingle => {
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const unit = units[data.random.next(units.length)];
      const x = data.random.next(1, 21);
      if (inverse) {
        return {
          texts: [
            `Krychle má objem ${x * x * x} ${unit}${Numerals.superScript(3)}.`,
            'Jakou má délku strany?',
          ],
          answer: `${x} ${unit}`,
        };
      }
      return {
        texts: [`Krychle má délku strany ${x} ${unit}.`, 'Jaký má objem?'],
        answer: `${x * x * x} ${unit}${Numerals.superScript(3)}`,
      };
    },

  volumeSphere:
    (inverse = false) =>
    (data: GenData): QSingle => {
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const unit = units[data.random.next(units.length)];
      const x = data.random.next(1, 10);
      const frac = Fraction.create(4 * x * x * x, 3)
        .simplify()
        .toNumber();
      if (inverse) {
        return {
          texts: [`Koule má objem ${frac}π ${unit}${Numerals.superScript(3)}.`, 'Jaký má poloměr?'],
          answer: `${x} ${unit}`,
        };
      }
      return {
        texts: [`Koule má poloměr ${x} ${unit}.`, 'Jaký má objem?'],
        answer: `${frac}π ${unit}${Numerals.superScript(3)}`,
      };
    },

  surfaceAreaSphere:
    (inverse = false) =>
    (data: GenData): QSingle => {
      const units = ['mm', 'cm', 'dm', 'm', 'km'];
      const unit = units[data.random.next(units.length)];
      const x = data.random.next(1, 10);
      if (inverse) {
        return {
          texts: [
            `Koule má povrch ${4 * x * x}π ${unit}${Numerals.superScript(2)}.`,
            'Jaký má poloměr?',
          ],
          answer: `${x} ${unit}`,
        };
      }
      return {
        texts: [`Koule má poloměr ${x} ${unit}.`, 'Jaký má povrch?'],
        answer: `${4 * x * x}π ${unit}${Numerals.superScript(2)}`,
      };
    },
};

export const Statistics = {
  average:
    () =>
    (data: GenData): QSingle => {
      const count = data.random.next(5, 11);
      const numbers = Array.from({ length: count }, () => data.random.next(1, 21));
      const sum = numbers.reduce((a, b) => a + b, 0);
      return {
        texts: [`Vypočítejte průměrnou hodnotu čísel: ${numbers.join(', ')}.`],
        answer: (sum / count).toLocaleString('en-US', { maximumFractionDigits: 2 }),
      };
    },

  median:
    () =>
    (data: GenData): QSingle => {
      const count = data.random.next(5, 11);
      const numbers = Array.from({ length: count }, () => data.random.next(1, 21)).sort(
        (a, b) => a - b
      );
      let median: number;
      if (count % 2 === 1) median = numbers[Math.floor(count / 2)];
      else median = (numbers[count / 2 - 1] + numbers[count / 2]) / 2;
      return {
        texts: [`Vypočítejte medián čísel: ${numbers.join(', ')}.`],
        answer: median.toLocaleString('en-US', { maximumFractionDigits: 2 }),
      };
    },

  mode:
    () =>
    (data: GenData): QSingle => {
      const count = data.random.next(5, 11);
      const numbers = Array.from({ length: count }, () => data.random.next(1, 11));
      const counts: Record<number, number> = {};
      numbers.forEach((n) => (counts[n] = (counts[n] || 0) + 1));
      const mode = Object.keys(counts).reduce((a, b) =>
        counts[parseInt(a)] > counts[parseInt(b)] ? a : b
      );
      return {
        texts: [`Vypočítejte modus čísel: ${numbers.join(', ')}.`],
        answer: mode,
      };
    },

  stdDev:
    (varianceOnly = false) =>
    (data: GenData): QSingle => {
      const count = data.random.next(5, 11);
      const numbers = Array.from({ length: count }, () => data.random.next(1, 21));
      const avg = numbers.reduce((a, b) => a + b, 0) / count;
      const variance = numbers.reduce((acc, n) => acc + (n - avg) ** 2, 0) / count;
      if (varianceOnly) {
        return {
          texts: [`Vypočítejte rozptyl čísel: ${numbers.join(', ')}.`],
          answer: variance.toLocaleString('en-US', { maximumFractionDigits: 2 }),
        };
      }
      return {
        texts: [`Vypočítejte směrodatnou odchylku čísel: ${numbers.join(', ')}.`],
        answer: Math.sqrt(variance).toLocaleString('en-US', { maximumFractionDigits: 2 }),
      };
    },

  correlation:
    () =>
    (data: GenData): QSingle => {
      const count = data.random.next(5, 11);
      const xValues = Array.from({ length: count }, () => data.random.next(1, 21));
      const yValues = xValues.map((x) => 2 * x + data.random.next(-5, 6));

      const avgX = xValues.reduce((a, b) => a + b, 0) / count;
      const avgY = yValues.reduce((a, b) => a + b, 0) / count;

      let covariance = 0;
      let sumSqX = 0;
      let sumSqY = 0;

      for (let i = 0; i < count; i++) {
        const dx = xValues[i] - avgX;
        const dy = yValues[i] - avgY;
        covariance += dx * dy;
        sumSqX += dx * dx;
        sumSqY += dy * dy;
      }
      covariance /= count;
      const stdDevX = Math.sqrt(sumSqX / count);
      const stdDevY = Math.sqrt(sumSqY / count);
      const correlation = covariance / (stdDevX * stdDevY);

      const pairs = xValues.map((x, i) => `(${x}, ${yValues[i]})`).join(', ');
      return {
        texts: ['Vypočítejte korelační koeficient pro následující dvojice hodnot:', `${pairs  }.`],
        answer: correlation.toLocaleString('en-US', { maximumFractionDigits: 2 }),
      };
    },
};

export class Math_Combinatorics__Combination extends SimpleGenSingle {
  constructor() {
    super(Combinatorics.combination(), 'Math_Combinatorics__Combination');
  }
}
export class Math_Combinatorics__Permutation extends SimpleGenSingle {
  constructor() {
    super(Combinatorics.permutation(), 'Math_Combinatorics__Permutation');
  }
}
export class Math_Combinatorics__Variation extends SimpleGenSingle {
  constructor() {
    super(Combinatorics.variation(), 'Math_Combinatorics__Variation');
  }
}

export class Math_Derivation__X2 extends SimpleGenSingle {
  constructor() {
    super(Derivation.base(3), 'Math_Derivation__X2');
  }
}
export class Math_Derivation__X3 extends SimpleGenSingle {
  constructor() {
    super(Derivation.base(4), 'Math_Derivation__X3');
  }
}
export class Math_Derivation__X4 extends SimpleGenSingle {
  constructor() {
    super(Derivation.base(5), 'Math_Derivation__X4');
  }
}
export class Math_Derivation__X5 extends SimpleGenSingle {
  constructor() {
    super(Derivation.base(6), 'Math_Derivation__X5');
  }
}

export class Math_Equation_Quadratic__1 extends SimpleGenSingle {
  constructor() {
    super(EquationsAdv.quadratic(), 'Math_Equation_Quadratic__1');
  }
}

export class Math_PythagoreanTheorem__FindHypotenuse extends SimpleGenSingle {
  constructor() {
    super(Geometry.pythagorasHypotenuse(), 'Math_PythagoreanTheorem__FindHypotenuse');
  }
}
export class Math_PythagoreanTheorem__FindLeg extends SimpleGenSingle {
  constructor() {
    super(Geometry.pythagorasLeg(), 'Math_PythagoreanTheorem__FindLeg');
  }
}

export class Math_Statistics__Average extends SimpleGenSingle {
  constructor() {
    super(Statistics.average(), 'Math_Statistics__Average');
  }
}
export class Math_Statistics__Median extends SimpleGenSingle {
  constructor() {
    super(Statistics.median(), 'Math_Statistics__Median');
  }
}
export class Math_Statistics__Mode extends SimpleGenSingle {
  constructor() {
    super(Statistics.mode(), 'Math_Statistics__Mode');
  }
}
export class Math_Statistics__StandardDeviation extends SimpleGenSingle {
  constructor() {
    super(Statistics.stdDev(), 'Math_Statistics__StandardDeviation');
  }
}
export class Math_Statistics__Variance extends SimpleGenSingle {
  constructor() {
    super(Statistics.stdDev(true), 'Math_Statistics__Variance');
  }
}
export class Math_Statistics__Correlation extends SimpleGenSingle {
  constructor() {
    super(Statistics.correlation(), 'Math_Statistics__Correlation');
  }
}

export class Math_SurfaceArea__Cube__Base extends SimpleGenSingle {
  constructor() {
    super(Geometry.surfaceAreaCube(), 'Math_SurfaceArea__Cube__Base');
  }
}
export class Math_SurfaceArea__Cube__Inverse extends SimpleGenSingle {
  constructor() {
    super(Geometry.surfaceAreaCube(true), 'Math_SurfaceArea__Cube__Inverse');
  }
}

export class Math_Volume__Cube__Base extends SimpleGenSingle {
  constructor() {
    super(Geometry.volumeCube(), 'Math_Volume__Cube__Base');
  }
}
export class Math_Volume__Cube__Inverse extends SimpleGenSingle {
  constructor() {
    super(Geometry.volumeCube(true), 'Math_Volume__Cube__Inverse');
  }
}
export class Math_Volume__Sphere__Base extends SimpleGenSingle {
  constructor() {
    super(Geometry.volumeSphere(), 'Math_Volume__Sphere__Base');
  }
}
export class Math_Volume__Sphere__Inverse extends SimpleGenSingle {
  constructor() {
    super(Geometry.volumeSphere(true), 'Math_Volume__Sphere__Inverse');
  }
}
export class Math_SurfaceArea__Sphere__Base extends SimpleGenSingle {
  constructor() {
    super(Geometry.surfaceAreaSphere(), 'Math_SurfaceArea__Sphere__Base');
  }
}
export class Math_SurfaceArea__Sphere__Inverse extends SimpleGenSingle {
  constructor() {
    super(Geometry.surfaceAreaSphere(true), 'Math_SurfaceArea__Sphere__Inverse');
  }
}
